// Global
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { ICategory } from '@/models/categories.model';
import { StyledFormItem, StyledTreeSelect } from './CategorySelectStyles';

// Helpers
import { TreeSelectDataNode, highlightText, filterTreeNode } from '../../helpers';
import { NO_SUB_CATEGORIES_POSTFIX } from '../../constants';
import { useGetRequiredRule } from '@/hooks';

type TCategorySelect = {
  categories: ICategory[] | null;
  autocompleteCategories: ICategory[] | null;
  expandedItems: Set<string>;
  onSearch: (value?: string) => void;
  handleExpandTreeItemData: (dataNode: TreeSelectDataNode) => Promise<unknown>;
  formItemName: string;
  handleCategoryChange: (value: unknown) => Promise<void>;
};

const CategorySelect = ({
  categories,
  autocompleteCategories,
  onSearch,
  expandedItems,
  handleExpandTreeItemData,
  formItemName,
  handleCategoryChange,
}: TCategorySelect) => {
  const { t } = useTranslation(['common', 'validations']);
  const requiredRule = useGetRequiredRule({ t });
  const [searchValue, setSearchValue] = useState<string>('');

  const onInputChange = (value: string) => {
    setSearchValue(value);
    onSearch(value);
  };

  const handleInputClear = () => {
    setSearchValue('');
  };

  const isPlaceholderCategory = (category: ICategory) => category.id.endsWith(NO_SUB_CATEGORIES_POSTFIX);

  const hasRealSubCategories = (subCategories?: ICategory[]) =>
    !!subCategories?.some((subCategory) => !isPlaceholderCategory(subCategory));

  const treeData = useMemo(() => {
    return (
      categories?.map((category) => {
        const hasRealChildren = hasRealSubCategories(category.sub_categories);

        return {
          value: category.id,
          title: category.name,
          isLeaf: !hasRealChildren,
          disabled: hasRealChildren,
          children: category.sub_categories?.map((subCategory) => {
            const subHasRealChildren = hasRealSubCategories(subCategory.sub_categories);

            if (isPlaceholderCategory(subCategory)) {
              return {
                value: subCategory.id,
                title: subCategory.name,
                isLeaf: true,
                disabled: true,
              };
            }

            return {
              value: subCategory.id,
              title: subCategory.name,
              isLeaf: !subHasRealChildren,
              disabled: subHasRealChildren,
              children: subCategory.sub_categories?.map((lastLevelItem) => ({
                value: lastLevelItem.id,
                title: lastLevelItem.name,
                isLeaf: true,
                disabled: isPlaceholderCategory(lastLevelItem),
              })),
            };
          }),
        };
      }) || []
    );
  }, [categories, expandedItems]);

  const flatData = useMemo(
    () =>
      autocompleteCategories
        ? autocompleteCategories.map((category) => ({
            value: category.id,
            title: category.name,
            isLeaf: true,
            disabled: hasRealSubCategories(category.sub_categories) || isPlaceholderCategory(category),
          }))
        : [],
    [autocompleteCategories],
  );

  const titleRender = (node: TreeSelectDataNode) => {
    return highlightText(node.title?.toString() || '', searchValue);
  };

  return (
    <StyledFormItem name={formItemName} label={t('category_select_label')} rules={[requiredRule]}>
      <StyledTreeSelect
        showSearch
        onSearch={onInputChange}
        onClear={handleInputClear}
        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
        allowClear
        treeDefaultExpandAll
        treeData={searchValue ? flatData : treeData}
        filterTreeNode={filterTreeNode}
        treeTitleRender={titleRender}
        loadData={handleExpandTreeItemData}
        onChange={handleCategoryChange}
      />
    </StyledFormItem>
  );
};

export default CategorySelect;
